<template>
  <div class="sb-navigation">
    <div class="position-relative py-3">
      <div
        v-for="(lv1_info, lv1_idx) in toc_list"
        :key="'toc_lv1_' + lv1_idx"
        class="navigation-group"
      >
        <div :class="['d-flex align-items-center mb-3 pl-3', { 'mt-4': lv1_idx !== 0 }]">
          <h5
            class="nav-title nav-title--parent"
            :data-observe-anchor="'toc_lv1_' + lv1_info.name[0] + lv1_idx"
            :class="{ 'navigation-indicator': lv1_idx === 0 }"
          >
            <b-link
              class="nav-anchor text-keep-all text-decoration-none"
              @click="scroll_to_title('toc_lv1_' + lv1_idx)"
            >
              <span v-show="lang === 'KOR'">{{ lv1_info.name }}</span>
              <span v-show="lang === 'ENG'">{{
                lv1_info.name_em ? lv1_info.name_em : lv1_info.name_ea
              }}</span>
            </b-link>
          </h5>
        </div>

        <h6
          v-for="(lv2_info, lv2_idx) in lv1_info.children"
          :key="'toc_lv2_' + lv2_idx"
          :class="['nav-title nav-title--child', 'mb-0', 'ml-3']"
          :data-observe-anchor="'toc_lv2_' + lv1_idx + lv2_idx"
        >
          <div v-if="lv2_info.children.length > 0">
            <b-link
              class="nav-anchor text-keep-all text-decoration-none"
              @click="scroll_to_title('toc_lv2_' + lv1_idx + lv2_idx)"
            >
              <span v-show="lang === 'KOR'">
                {{ lv2_info.name }}
              </span>
              <span v-show="lang === 'ENG'">{{
                lv2_info.name_em ? lv2_info.name_em : lv2_info.name_ea
              }}</span>
            </b-link>
          </div>
        </h6>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'DCTocViewer',
  computed: {
    ...mapGetters('report_list', ['selected_report']),
    ...mapState('charts', ['toc_list']),
    ...mapState('report_list', ['lang']),
    anchorList() {
      return this.toc_list.reduce((_anchors, lv1_info, lv1_idx) => {
        if (lv1_info) {
          _anchors.push(`#toc_lv1_${lv1_idx}`);
        }

        if (lv1_info.children && lv1_info.children.length > 0) {
          lv1_info.children.forEach((lv2_info, lv2_idx) => {
            if (lv2_info.children && lv2_info.children.length > 0) {
              _anchors.push(`#toc_lv2_${lv1_idx}${lv2_idx}`);
            }
          });
        }
        return _anchors;
      }, []);
    }
  },
  // TODO(#736 follow-up): GriTocViewer/SASBTocViewer/TCFDTocViewer/StoryBoardTocViewer와 동일 패턴 — mixin 추출 권장.
  mounted() {
    window.addEventListener('scroll', this.handleWindowScroll);
    this.setupAnchorObserver();
  },
  beforeDestroy() {
    if (this.anchorObserver) {
      this.anchorObserver.disconnect();
      this.anchorObserver = null;
    }
    if (this.observerTimerId) {
      clearTimeout(this.observerTimerId);
      this.observerTimerId = null;
    }
    window.removeEventListener('scroll', this.handleWindowScroll);
  },
  data() {
    return {
      activatedAnchor: null,
      anchorObserver: null,
      intersectingAnchors: new Set(),
      observerTimerId: null
    };
  },
  methods: {
    setupAnchorObserver() {
      if (!this.anchorList) return;
      if (this.observerTimerId) clearTimeout(this.observerTimerId);
      this.observerTimerId = setTimeout(() => {
        this.observerTimerId = null;
        if (this.anchorObserver) {
          this.anchorObserver.disconnect();
        }
        this.intersectingAnchors.clear();
        this.anchorObserver = new IntersectionObserver(this.handlerObserveAnchor, {
          rootMargin: '-105px 0px -60% 0px',
          threshold: 0
        });
        this.anchorList.forEach(_anchor => {
          const _anchorTitle = document.querySelector(_anchor);
          if (_anchorTitle) {
            this.anchorObserver.observe(_anchorTitle);
          }
        });
        const initialNav = document.querySelector('.navigation-indicator');
        const color = this.selected_report && this.selected_report.main_color;
        if (initialNav && color) {
          initialNav.style.color = color;
          initialNav.style.borderLeft = `4px solid ${color}`;
        }
      }, 400);
    },
    setActiveIndicator(anchorId) {
      if (!anchorId || this.activatedAnchor === anchorId) return;
      const _activedNavs = document.querySelectorAll('.navigation-indicator');
      _activedNavs.forEach(_activedNav => {
        _activedNav.classList.remove('navigation-indicator');
        _activedNav.style.color = '';
        _activedNav.style.borderLeft = '';
      });
      const _navToAnchor = document.querySelector(`[data-observe-anchor="${anchorId}"]`);
      if (_navToAnchor) {
        this.activatedAnchor = anchorId;
        const color = this.selected_report && this.selected_report.main_color;
        if (color) {
          const node = _navToAnchor.childNodes[0];
          node.classList.add('navigation-indicator');
          node.style.color = color;
          node.style.borderLeft = `4px solid ${color}`;
        }
      }
    },
    handlerObserveAnchor(entries) {
      entries.forEach(entry => {
        const anchorId = entry.target.id.replace(/#/g, '');
        if (entry.isIntersecting) {
          this.intersectingAnchors.add(anchorId);
        } else {
          this.intersectingAnchors.delete(anchorId);
        }
      });
      if (this.intersectingAnchors.size === 0) return;
      for (const anchor of this.anchorList) {
        const id = anchor.replace('#', '');
        if (this.intersectingAnchors.has(id)) {
          this.setActiveIndicator(id);
          break;
        }
      }
    },
    handleWindowScroll() {
      if (!this.anchorList || this.anchorList.length === 0) return;
      const atTop = window.scrollY <= 5;
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 5;
      if (atTop) {
        this.setActiveIndicator(this.anchorList[0].replace('#', ''));
      } else if (atBottom) {
        this.setActiveIndicator(this.anchorList[this.anchorList.length - 1].replace('#', ''));
      }
    },
    scroll_to_title(link) {
      const element = document.getElementById(link);
      if (element) {
        element.style.scrollMarginTop = '100px';
        element.scrollIntoView({ behavior: 'smooth' });
        this.setActiveIndicator(link);
      }
    }
  }
};
</script>

<style></style>
